import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { useLocation, useNavigate } from 'react-router';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { determinePath } from '@jwp/ott-common/src/utils/urlFormatting';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useMenuItems } from '@jwp/ott-hooks-react/src/useMenuItems';

import Button from '../../components/Button/Button';
import Sidebar from '../../components/Sidebar/Sidebar';
import MenuButton from '../../components/MenuButton/MenuButton';
import { modalURLFromLocation } from '../../utils/location';
import UserMenuNav from '../../components/UserMenuNav/UserMenuNav';

import styles from './SidebarContainer.module.scss';

const SidebarUserActions = ({
  sideBarOpen,
  canLogin,
  isLoggedIn,
  favoritesEnabled,
  paymentsEnabled,
  manageSubscriptionEnabled,
  onManageSubscriptionClick,
  onLoginClick,
  onSignUpClick,
  onLogoutClick,
}: {
  sideBarOpen: boolean;
  canLogin: boolean;
  isLoggedIn: boolean;
  favoritesEnabled: boolean;
  paymentsEnabled: boolean;
  manageSubscriptionEnabled: boolean;
  onLoginClick: () => void;
  onSignUpClick: () => void;
  onLogoutClick: () => void;
  onManageSubscriptionClick: () => void;
}) => {
  const { t } = useTranslation(['menu', 'user']);
  const userMenuTitleId = useOpaqueId('usermenu-title');

  // Fallback log out button when we don't expect the user to be signed in
  if (!canLogin && isLoggedIn) {
    return <MenuButton onClick={onLogoutClick} label={t('user:nav.logout')} />;
  }

  if (!canLogin) return null;

  return isLoggedIn ? (
    <section aria-labelledby={userMenuTitleId}>
      <UserMenuNav
        focusable={sideBarOpen}
        favoritesEnabled={favoritesEnabled}
        titleId={userMenuTitleId}
        paymentsEnabled={paymentsEnabled}
        manageSubscriptionEnabled={manageSubscriptionEnabled}
        onManageSubscriptionClick={onManageSubscriptionClick}
      />
    </section>
  ) : (
    <div className={styles.buttonContainer}>
      <Button tabIndex={sideBarOpen ? 0 : -1} onClick={onLoginClick} label={t('sign_in')} aria-haspopup="dialog" fullWidth />
      <Button
        tabIndex={sideBarOpen ? 0 : -1}
        variant="contained"
        color="primary"
        onClick={onSignUpClick}
        label={t('sign_up')}
        aria-haspopup="dialog"
        data-testid="sign_up"
        fullWidth
      />
    </div>
  );
};

const SidebarContainer = ({ disableCustomerPortal = false }: { disableCustomerPortal?: boolean }) => {
  const { t } = useTranslation('common');
  const translationKey = useTranslationKey('label');
  const navigate = useNavigate();
  const location = useLocation();

  const sideBarOpen = useUIStore((state) => state.sideBarOpen);
  const {
    config: { features },
    accessModel,
  } = useConfigStore((state) => ({
    config: state.config,
    accessModel: state.accessModel,
  }));
  const isLoggedIn = useAccountStore(({ user }) => !!user);

  const { data: menu = [] } = useMenuItems();

  const accountController = getModule(AccountController);
  const customerPortalEnabled = accountController.getCustomerPortalEnabled();
  const manageSubscriptionEnabled = customerPortalEnabled && !disableCustomerPortal;
  // @CUSTOMER vd-ott-iq disable payments page
  const paymentsEnabled = false;
  const favoritesEnabled = !!features?.favoritesList;
  const canLogin = accessModel !== ACCESS_MODEL.AVOD;

  const closeSideBar = () => useUIStore.setState({ sideBarOpen: false });

  const loginButtonClickHandler = () => {
    navigate(modalURLFromLocation(location, 'login'));
  };

  const signUpButtonClickHandler = () => {
    navigate(modalURLFromLocation(location, 'create-account'));
  };

  const logoutButtonClickHandler = () => {
    accountController.logout();
  };

  const manageSubscriptionClickHandler = async () => {
    const hasSubscription = !!useAccountStore.getState().subscription;

    if (!hasSubscription) {
      return navigate(modalURLFromLocation(location, 'choose-offer'));
    }

    const customerPortalUrl = await accountController.getCustomerPortalUrl(window.location.href);

    if (customerPortalUrl) {
      window.location.href = customerPortalUrl;
    }
  };

  return (
    <Sidebar isOpen={sideBarOpen} onClose={closeSideBar}>
      <ul>
        <li>
          <MenuButton label={t('home')} to="/" />
        </li>
        {menu.map(({ contentId, type, label, custom }) => (
          <li key={contentId}>
            <MenuButton label={custom?.[translationKey] || label} to={determinePath({ type, contentId })} />
          </li>
        ))}
      </ul>
      <hr className={styles.separator} />
      <SidebarUserActions
        sideBarOpen={sideBarOpen}
        favoritesEnabled={favoritesEnabled}
        canLogin={canLogin}
        isLoggedIn={isLoggedIn}
        paymentsEnabled={paymentsEnabled}
        manageSubscriptionEnabled={manageSubscriptionEnabled}
        onManageSubscriptionClick={manageSubscriptionClickHandler}
        onLoginClick={loginButtonClickHandler}
        onSignUpClick={signUpButtonClickHandler}
        onLogoutClick={logoutButtonClickHandler}
      />
    </Sidebar>
  );
};

export default SidebarContainer;
